/**
 * Ion Conversation Service
 *
 * Wraps the conversation REST endpoints exposed by the chat backend:
 *
 *   GET    /api/v1/chat/conversations           — paginated list (newest-first)
 *   GET    /api/v1/chat/conversations/{id}/messages — full message history
 *   DELETE /api/v1/chat/conversations/{id}      — soft-delete
 *
 * Hydration: `loadConversationIntoStore(id)` fetches the full message
 * history and rewrites the global chatStore so the slide-over /
 * fullpage continues seamlessly. Any in-flight stream is aborted before
 * the swap to avoid orphaned mutations into a dead conversation.
 *
 * Listing + delete is owned by `ConversationHistoryDrawer` — we deliberately
 * don't keep a global "conversation list" signal. The list is only relevant
 * while the drawer is open and re-fetching on each open keeps the UI honest
 * about server state without a stale-cache problem.
 */

import { authenticatedFetch } from './authService.js'
import { mapApiMessage } from './chatMessageMapper.js'
import {
  abortController,
  applySelectionContextFromArtifact,
  chatMessages,
  currentConversationId,
  discardStaleConversation,
  pinLatestArtifactFromMessages,
  reasoningOpenFor,
  setCurrentConversationId,
  stampMessagesIdentity,
  streamingMessageId,
  chatStreaming,
} from '../state/chatStore.js'

const BASE = '/api/v1/chat/conversations'

/**
 * Fetch the conversation list. Returns `{conversations, nextBefore}` on
 * success, throws on failure.
 *
 * @param {object} [opts]
 * @param {number} [opts.limit=30]
 * @param {string} [opts.before]  ISO-8601 cursor for pagination
 */
export async function listConversations({ limit = 30, before = null } = {}) {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  if (before) params.set('before', before)

  const response = await authenticatedFetch(`${BASE}?${params.toString()}`)
  if (!response.ok) {
    throw new Error(`http_${response.status}`)
  }
  const data = await response.json()
  return {
    conversations: Array.isArray(data.conversations) ? data.conversations : [],
    nextBefore: data.nextBefore || null,
  }
}

/**
 * Fetch the full message history for a conversation.
 */
export async function getConversationMessages(publicId) {
  if (!publicId) throw new Error('missing_id')
  const response = await authenticatedFetch(
    `${BASE}/${encodeURIComponent(publicId)}/messages`,
  )
  if (!response.ok) {
    throw new Error(`http_${response.status}`)
  }
  return response.json()
}

/**
 * Submit admin feedback on a turn or whole conversation (admin-only;
 * the backend guards with require_admin_role). The backend derives the
 * routing area + owner when `area` is omitted.
 *
 * @param {object} payload
 * @param {string} payload.conversationId
 * @param {'message'|'conversation'} payload.scope
 * @param {number|null} [payload.targetTurnIndex] — 0-based assistant-turn ordinal
 * @param {string} payload.category
 * @param {string|null} [payload.area]
 * @param {string} payload.severity
 * @param {string} payload.comment
 * @param {object|null} [payload.pageContext] — PageContext-shaped snapshot
 * @param {string|null} [payload.screenshot] — optional image as a data URL
 */
export async function submitIonFeedback(payload) {
  const response = await authenticatedFetch('/api/v1/chat/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) {
    throw new Error(`http_${response.status}`)
  }
  return response.json()
}

/**
 * Soft-delete a conversation server-side.
 */
export async function deleteConversation(publicId) {
  if (!publicId) throw new Error('missing_id')
  const response = await authenticatedFetch(
    `${BASE}/${encodeURIComponent(publicId)}`,
    { method: 'DELETE' },
  )
  if (!response.ok && response.status !== 204) {
    throw new Error(`http_${response.status}`)
  }
  // If the user just deleted their active conversation, drop the local id so
  // the next send starts a fresh one.
  if (currentConversationId.peek() === publicId) {
    currentConversationId.value = null
  }
}

/**
 * Server-driven hydration: replaces the in-store messages with the
 * conversation's persisted history. Aborts any active stream first.
 *
 * Maps the backend payload to the richer FE message shape. Tool calls
 * ARE persisted (migration 20260522_spark_msg_tool_calls) and returned
 * by the messages endpoint (redacted per role), so we rehydrate them
 * here — without this, every [[VALUE:<id>#<ref>]] chip and every inline
 * plot/table artifact resolves against an EMPTY toolCalls list on reload
 * and renders as a broken "unverified reference" (the live stream looked
 * fine only because the calls were still in memory). Shape mirrors
 * chatStore.startToolCall + the live useChatStream loader so
 * MarkdownRenderer.hydrateMarkers / _resolveMultiValue need no
 * special-casing.
 */
export async function loadConversationIntoStore(publicId) {
  if (!publicId) return { ok: false, error: 'missing_id' }

  // Abort any in-flight stream before swapping state.
  const ac = abortController.peek()
  if (ac && !ac.signal.aborted) {
    try {
      ac.abort()
    } catch (e) {
      /* noop */
    }
  }

  try {
    const data = await getConversationMessages(publicId)
    // Shared mapper (chatMessageMapper) — identical shape to the live
    // useChatStream loader, incl. rehydrated toolCalls WITH their output
    // envelopes (so [[V:N]] chips + plots resolve on reopen).
    const hydrated = (data.messages || []).map(mapApiMessage)
    chatMessages.value = hydrated
    streamingMessageId.value = null
    chatStreaming.value = false
    abortController.value = null
    // Re-pin the conversation's last renderable artifact so charts/tables
    // reappear in the canvas on reopen (sets pinnedArtifact internally —
    // null when the conversation produced none).
    pinLatestArtifactFromMessages(hydrated)
    // Seed the AUTHORITATIVE persisted selection_context OVER the artifact-
    // derived heuristic above. Message-history artifacts carry provenance /
    // weights as of when they were emitted, which lags later state persisted
    // server-side (e.g. a constraint the user confirmed after the last ranked
    // table, or a weight the /re-rank endpoint saved without appending a new
    // artifact). The blob is snake_case, matching the streamed selection_context
    // shape applySelectionContextFromArtifact already handles.
    if (data.selectionContext && typeof data.selectionContext === 'object') {
      applySelectionContextFromArtifact({ selection_context: data.selectionContext })
    }
    reasoningOpenFor.value = new Set()
    setCurrentConversationId(data.conversationId || publicId)
    // Record WHOSE messages these are, so an account switch in the same tab
    // forces a re-fetch instead of trusting a non-empty message list.
    stampMessagesIdentity()
    return { ok: true, count: hydrated.length }
  } catch (err) {
    const code = err?.message || 'network'
    // The server says this conversation isn't ours: 404 = unknown, foreign, or
    // soft-deleted for this account; 403 = access revoked. Drop the stored id
    // AND the stale messages rather than leaving them in place — otherwise the
    // next POST /chat/stream re-sends a foreign conversation id (which used to
    // raise a 500 server-side) and the UI keeps showing the previous account's
    // thread. The unsent draft is deliberately preserved.
    if (code === 'http_404' || code === 'http_403') {
      discardStaleConversation()
    }
    return { ok: false, error: code }
  }
}
